<template>
  <layout-report :isProgressDone="true">
    <v-container v-if="isLoading">
      <v-skeleton-loader
        class="mx-auto"
        type="card-heading, list-item-two-line, image, card-heading, list-item-two-line, article, actions"
      ></v-skeleton-loader>
    </v-container>
    <v-container class="pb-14" v-else>
      <v-card flat class="card-outer" v-if="!isError">
        <v-card-title>{{ signRequest.title }}</v-card-title>
        <v-card-text v-if="signRequest.description" v-html="signRequest.description"> </v-card-text>
        <v-card-text v-else>
          {{ $t("sign.Sign.description", { companyName: partner.companyName }) }}
        </v-card-text>

        <v-row class="pt-8" v-if="signRequest.documents">
          <v-col cols="12" md="7">
            <v-row v-for="document in signRequest.documents" :key="document">
              <v-col cols="12">
                <iframe
                  @click="document"
                  title="pdf"
                  :src="document"
                  :style="`border: none; width:100%; height: ${heightIframe}`"
                  class="pdf"
                  id="pdf"
                  ref="pdf"
                  v-show="isPdfLoaded"
                  @load="load"
                />

                <v-skeleton-loader v-if="!isPdfLoaded" class="mx-auto" type="image" />
                <v-tooltip bottom nudge-right>
                  <template v-slot:activator="{ on }">
                    <v-btn
                      outlined
                      inverted
                      color="primary"
                      class="mb-2"
                      :href="document"
                      target="_blank"
                      v-on="on"
                      v-if="$vuetify.breakpoint.mobile"
                      block
                    >
                      {{ $t("sign.Sign.openFile") }}

                      <v-icon>mdi-download</v-icon>
                    </v-btn>
                    <v-btn class="mb-2" icon :href="document" target="_blank" v-on="on" v-else>
                      <v-icon>mdi-download</v-icon>
                    </v-btn>
                  </template>
                  <span>{{ $t("sign.Sign.download") }}</span>
                </v-tooltip>
              </v-col>
            </v-row>
          </v-col>
          <v-col cols="12" md="5">
            <v-form v-model="isValid" ref="signDocumentForm" v-if="!isLoading">
              <v-row>
                <v-card-text class="pl-0 pt-2">
                  <span v-html="$t('sign.Sign.privacy')" />
                  <span
                    v-if="partner && partner.settings && partner.settings.agbUrl.link"
                    v-html="$t('sign.Sign.terms', { agb: partner.settings.agbUrl.link })"
                  />
                  <span v-else>.</span>
                </v-card-text>

                <v-col cols="12" sm="6" md="12" lg="6" :class="firstNameSmallClass">
                  <v-text-field
                    outlined
                    dense
                    v-model="signature.firstName"
                    :label="$t('sign.Sign.firstName')"
                    :rules="requiredRule"
                  ></v-text-field>
                </v-col>
                <v-col cols="12" sm="6" md="12" lg="6">
                  <v-text-field
                    outlined
                    dense
                    v-model="signature.lastName"
                    :label="$t('sign.Sign.lastName')"
                    :rules="requiredRule"
                  ></v-text-field>
                </v-col>

                <v-col cols="12" class="pt-0 mt-n8">
                  <v-text-field
                    outlined
                    v-model="signature.location"
                    :label="$t('sign.Sign.location')"
                    :rules="requiredRule"
                  />
                </v-col>

                <v-col cols="12" class="pt-0 mt-n8">
                  <span class="pa-1 overlay" :style="overlayStyle"> {{ $t("sign.Sign.signOverlay") }}</span>

                  <vue-signature-pad
                    id="signature"
                    ref="signaturePad"
                    width="100%"
                    :height="heightSignature"
                    class="mt-2"
                  />
                  <span class="signLine unselectable"></span>
                  <v-row class="pt-4 pl-4 pr-4">
                    <span class="pt-1"> {{ new Date() | simpleDoubleDigitDate }} </span>
                    <v-spacer />
                    <v-tooltip left nudge-left>
                      <template v-slot:activator="{ on }">
                        <v-btn @click="clear" small icon v-on="on"><v-icon>mdi-delete-outline</v-icon></v-btn>
                      </template>
                      <span>{{ $t("sign.Sign.clear") }}</span>
                    </v-tooltip>
                  </v-row>
                </v-col>
                <v-col>
                  <v-checkbox
                    dense
                    v-model="terms"
                    :label="$t('sign.Sign.confirmationTerms')"
                    :rules="[(v) => !!v || $t('sign.Sign.readTerms')]"
                    ref="terms"
                    data-test-terms-checkbox
                  >
                  </v-checkbox>

                  <v-checkbox
                    dense
                    v-model="withdrawal"
                    :label="$t('sign.Sign.confirmationWithdrawal')"
                    :rules="[(v) => !!v || $t('sign.Sign.readWithdrawal')]"
                    ref="withdrawal"
                    data-test-withdrawal-checkbox
                  >
                  </v-checkbox>
                </v-col>
              </v-row>
            </v-form>

            <v-spacer />
            <v-btn
              :disabled="!isValid"
              color="success"
              block
              x-large
              min-width="250px"
              @click="send"
              :loading="isSaving"
            >
              {{ $t("sign.Sign.submit") }}
            </v-btn>
          </v-col>
        </v-row>

        <debug :debug="signRequest" />
      </v-card>
      <v-card flat v-else>
        <v-card-title class="justify-center">
          {{ $t(errorTitle) }}
        </v-card-title>
        <v-card-text>
          <v-tooltip bottom nudge-right>
            <template v-slot:activator="{ on }">
              <v-img
                class="mx-auto"
                src="@/assets/undraw/undraw_authentication_fsn5.svg"
                height="300"
                contain
                v-on="on"
              ></v-img>
            </template>
            <span>{{ $t("sign.Sign.noData") }}</span>
          </v-tooltip>
        </v-card-text>
        <v-card-actions class="justify-center">
          <a :href="'mailto:' + partner.settings.contact.email">
            {{ $t("sign.Sign.error.message", { companyName: partner.companyName }) }}
          </a>
        </v-card-actions>
      </v-card>
    </v-container>
  </layout-report>
</template>

<script lang="ts">
import Debug from "@/components/utility/Debug.vue";
import LayoutReport from "@/layouts/LayoutReport.vue";
import TheLayoutBase from "@/layouts/TheLayoutBase.vue";
import { BadRequestException, ConflictException, NotFoundException } from "@/lib/exceptions/http";
import { dataURItoBlob } from "@/lib/files/imageResizer";
import { getDefaultPartnerColor } from "@/lib/getDefaultPartnerColor";
import { reportHomeQueryParams } from "@/lib/queryParams/reportHomeQueryParams";
import { requiredRule } from "@/lib/rules/requiredRule";
import { simpleDoubleDigitDate } from "@/lib/utility/date-helper";
import { createFileName, FileType } from "@/lib/utility/string-helper";
import { SignCreateSignDtoGen } from "@/services/sign/v1/data-contracts";
import { ReportModule } from "@/store/modules/report.store";
import { SignCreateModule } from "@/store/modules/sign-create.store";
import { Component, Ref, Vue } from "vue-property-decorator";
import VueSignaturePad from "vue-signature-pad";

@Component({
  components: {
    TheLayoutBase,
    LayoutReport,
    Debug
  },
  filters: {
    simpleDoubleDigitDate
  }
})
export default class Sign extends Vue {
  isLoading = false;
  isSaving = false;
  isValid = false;

  isError = false;

  isPdfLoaded = false;

  errorTitle = "sign.Sign.error.title";

  terms = false;
  withdrawal = false;

  signature: SignCreateSignDtoGen = {
    lastName: ReportModule.user.lastName || "",
    firstName: ReportModule.user.firstName || "",
    location: ""
  };

  get isErrorGetter() {
    return this.isError;
  }

  get color() {
    return getDefaultPartnerColor();
  }

  get partnerColorDarkmode() {
    return this.$vuetify.theme.dark ? "white" : this.color;
  }

  get signRequest() {
    return SignCreateModule.request;
  }

  get partner() {
    return ReportModule.partner;
  }

  get requiredRule() {
    return [requiredRule()];
  }

  get heightIframe() {
    if (this.$vuetify.breakpoint.smAndDown) {
      return "350px";
    } else {
      return "600px";
    }
  }

  get heightSignature() {
    if (this.$vuetify.breakpoint.smAndDown) {
      return "150px";
    } else {
      return "200px";
    }
  }

  get firstNameSmallClass() {
    if (this.$vuetify.breakpoint.mdAndDown) {
      return "pb-0";
    } else {
      return "";
    }
  }
  @Ref("pdf")
  pdf!: HTMLIFrameElement;

  @Ref("signaturePad")
  signaturePad!: VueSignaturePad;

  cancel() {
    this.clear();

    this.signature.firstName = "";
    this.signature.lastName = "";

    this.terms = false;
  }

  clear() {
    this.signaturePad.clearSignature();
  }

  load() {
    this.isPdfLoaded = true;
  }

  async send() {
    const { isEmpty, data } = this.signaturePad.saveSignature();

    if (isEmpty) {
      this.$toast.error(this.$t("sign.Sign.error.missingSignature"));
      this.$log.error("Missing Signature");
      return;
    }

    try {
      this.isSaving = true;

      const fileName = createFileName(this.signature.lastName, FileType.PNG);
      const blob = dataURItoBlob(data);
      const image = new File([blob], fileName, { type: blob.type });

      this.signature.image = image;

      await SignCreateModule.create({
        partnerId: this.signRequest.partnerId,
        requestId: this.signRequest.requestId,
        data: this.signature
      });

      this.$toast.success(this.$t("sign.Sign.success"));
      this.errorTitle = "sign.Sign.error.conflict";
      this.isError = true;
    } catch (error) {
      this.$log.error(error);
      this.$toast.error(this.$t("sign.Sign.error.network"));
    } finally {
      this.isSaving = false;
    }
  }

  get overlayStyle() {
    if (this.$vuetify.theme.dark) {
      return "background-color:#1E1E1E;";
    } else {
      return "color: rgba(0, 0, 0, 0.6);";
    }
  }

  beforeCreate() {
    try {
      reportHomeQueryParams.forEach((param) => {
        const value = this.$route.query[param.key];

        if (value) {
          param.onFound((value as string) || "");
          this.$log.info(`${param.key} set in store`);
        }
      });
    } catch (error) {
      this.$log.error(error);
    }
  }

  async mounted() {
    this.isLoading = true;

    try {
      const requestId = this.$route.params.signRequestId;
      await SignCreateModule.find({ partnerId: this.partner._id, requestId });
    } catch (error) {
      this.$log.error(error);

      if (error instanceof ConflictException) {
        this.$toast.success(this.$t("sign.Sign.error.conflict"));
        this.errorTitle = "sign.Sign.error.conflict";
      } else if (error instanceof BadRequestException) {
        this.errorTitle = "sign.Sign.error.title";
        this.$toast.error(this.$t("sign.Sign.error.title"));
      } else if (error instanceof NotFoundException) {
        this.errorTitle = "sign.Sign.error.404";
        this.$toast.error(this.$t("sign.Sign.error.404"));
      }

      this.isError = true;
    } finally {
      this.isLoading = false;
    }
  }
}
</script>

<style scoped>
#signature {
  border: 2px solid rgb(192, 192, 192);
  border-radius: 4px;
  background-origin: border-box;
  background-clip: content-box, border-box;
  background-color: transparent;
}

.overlay {
  position: relative;
  top: 20px;
  left: 8px;
  background: white;
}

.overlayIcon {
  position: relative;
  top: 20px;
  left: 78%;
  background: white;
}

.signLine {
  position: relative;
  top: -20px;
  left: 20px;
  border-top: gray;
  border-bottom-style: ridge;
  width: 90%;
  display: block;
  color: rgb(192, 192, 192);
}

.unselectable {
  cursor: not-allowed;
  -webkit-touch-callout: none;
  -webkit-user-select: none;
  -khtml-user-select: none;
  -moz-user-select: none;
  -ms-user-select: none;
}
</style>
